import { type Price } from '../../../types/isomorphic';
import useOnLoad from '../../hooks/queries/useOnLoad';
import useSelectedIndex from '../../hooks/stateful/useSelectedIndex';
import { defaultSizeIndex } from '../defaultSizeIndex';

const STANDARD_LICENSE = 'standardlicense';

type LicenseOption = {
  type?: string;
  sizes?: Price[];
};

/**
 * Returns the standardlicense sizes and the size the user has selected among
 * them. Falls back to the default size when the selected
 * option is not standardlicense or the selected size index is out of range.
 * `selectSizeByName` selects the standardlicense option and the named size
 * together, so a choice made from another option takes effect.
 */
const useSelectedStandardLicenseSize = () => {
  const { response: { config: { options = [] } = {} } = {} } = useOnLoad() as {
    response?: { config?: { options?: LicenseOption[] } };
  };
  const {
    selectedOption,
    selectedOptionIndex,
    selectedSizeIndex,
    setSelectedOptionIndex,
    setSelectedSizeIndex,
  } = useSelectedIndex();

  const isStandardLicenseSelected = selectedOption?.type === STANDARD_LICENSE;
  const standardLicenseOptionIndex = isStandardLicenseSelected
    ? selectedOptionIndex
    : options.findIndex((option) => option.type === STANDARD_LICENSE);
  const sizes = options[standardLicenseOptionIndex]?.sizes ?? [];
  const sizeIndex =
    isStandardLicenseSelected &&
    typeof selectedSizeIndex === 'number' &&
    sizes[selectedSizeIndex]
      ? selectedSizeIndex
      : defaultSizeIndex(sizes);

  const selectSizeByName = (name: string) => {
    const index = sizes.findIndex((size) => size.name === name);
    if (index < 0) return;
    if (!isStandardLicenseSelected) {
      setSelectedOptionIndex(standardLicenseOptionIndex);
    }
    setSelectedSizeIndex(index);
  };

  return {
    sizes,
    selectedSizeIndex: sizeIndex,
    selectedSize: sizes[sizeIndex] as Price | undefined,
    selectSizeByName,
  };
};

export default useSelectedStandardLicenseSize;
